export interface PreviewStats {
  totalJobs: number;
  verified: number;
  discovering: number;
  pending: number;
  retryWait: number;
  blocked: number;
  assets: number;
  bytes: number;
  completedSeries: number;
}

export interface SeriesSummary {
  seriesId: string;
  title: string;
  description: string | null;
  totalEpisodes: number;
  verifiedEpisodes: number;
  posterKey: string;
}

export interface Episode {
  episodeId: string;
  episodeNumber: number;
  variants: { main: string; fallback: string };
  subtitles: Subtitle[];
}

export interface Subtitle {
  language: string;
  displayName: string;
  vttKey: string;
}

export interface SeriesDocument extends SeriesSummary {
  episodes: Episode[];
}

interface ApiEnvelope<T> { success: boolean; data: T; error?: string }
export interface SeriesPage {
  data: SeriesSummary[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export function seriesQuery(options: { page: number; limit: number; q: string }): string {
  const query = new URLSearchParams({ page: String(options.page), limit: String(options.limit) });
  if (options.q) query.set("q", options.q);
  return `/api/series?${query.toString()}`;
}

export function formatBytes(bytes: number): string {
  if (bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1_000)), units.length - 1);
  const value = bytes / 1_000 ** unit;
  return `${unit === 0 ? value.toFixed(0) : value.toFixed(2)} ${units[unit]}`;
}

export function formatCount(value: number): string {
  return new Intl.NumberFormat("vi-VN").format(value);
}

export async function login(password: string): Promise<void> {
  await apiFetch<{ success: true }>("/api/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  }, false);
}

export async function logout(): Promise<void> {
  await apiFetch<{ success: true }>("/api/logout", { method: "POST" }, false);
}

export async function getStats(): Promise<{ data: PreviewStats; publishedAt: string }> {
  return apiFetch("/api/stats");
}

export async function getSeries(page: number, q: string): Promise<SeriesPage> {
  const response = await apiFetch<ApiEnvelope<SeriesSummary[]> & { pagination: SeriesPage["pagination"] }>(seriesQuery({ page, limit: 24, q }));
  return { data: response.data, pagination: response.pagination };
}

export async function getSeriesDetail(seriesId: string): Promise<SeriesDocument> {
  return (await apiFetch<ApiEnvelope<SeriesDocument>>(`/api/series/${encodeURIComponent(seriesId)}`)).data;
}

async function apiFetch<T>(path: string, init?: RequestInit, redirectOnUnauthorized = true): Promise<T> {
  const response = await fetch(path, init);
  if (response.status === 401 && redirectOnUnauthorized) {
    window.location.assign("/login");
    throw new Error("Session expired");
  }
  const body = await response.json().catch(() => null) as ({ error?: string } & T) | null;
  if (!response.ok) throw new Error(body?.error ?? `Request failed (${response.status})`);
  if (!body) throw new Error("Invalid API response");
  return body;
}
